import { CommonModule } from '@angular/common';
import {
  Component,
  ViewChildren,
  ComponentRef,
  OnDestroy,
  ViewContainerRef,
  AfterViewInit,
  QueryList,
  input,
  output,
  signal,
  computed,
  inject,
  effect,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { MatExpansionModule } from '@angular/material/expansion';
import { WORKFLOW_COMPONENTS } from '../automation-editor/component-loader/definitions';
import { CommonActionPanelComponent } from '../automation-editor/components/common/common-action-panel-component.directive';
import { CommonTriggerPanelComponent } from '../automation-editor/components/common/common-trigger-panel.component';
import { type Automation, type WorkflowStepInterface, type StepDataInterface, RunningState } from '@vendasta/automata';
import { AutomationNodeIconComponent } from '../common/components/automation-node-icon/automation-node-icon.component';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { AutomationErrorHandlerService } from '../automation-editor/services/automation-error-handler.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Observable, combineLatest, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { DisplayService } from '../automation-editor/components/common/display.service';
import { TaskErrorDisclaimerComponent } from '../automation-editor/components/common/controls/task-error-disclaimer/task-error-disclaimer.component';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { ERROR_ICON_COLOR } from '../common/constants';
import { AutomationEditorComponent } from '../automation-editor/automation-editor.component';
import { MatSidenavModule } from '@angular/material/sidenav';
import { AutomationsSideDrawerService } from '../automation-editor/side-drawer/automations-side-drawer-service';
import { MatMenuModule } from '@angular/material/menu';

export interface SimplifiedAutomationEditorEvent {
  automation: Automation;
}

export interface SimplifiedAutomationRunStateChange {
  automation: Automation;
  turnOn: boolean;
}

export interface SimplifiedWorkflowInput {
  stepData: StepDataInterface[];
  unableToLoadData?: boolean;
}

interface TaskDefinitionGroup {
  taskDefinitionId: string;
  icon: string;
  iconColor: string;
  displayName: string;
  stepData: StepDataInterface[];
  componentType: 'trigger' | 'action';
  workflowStep: WorkflowStepInterface | null;
}

@Component({
  selector: 'automata-simplified-automation-editor',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    GalaxyAlertModule,
    GalaxyBadgeModule,
    MatExpansionModule,
    AutomationNodeIconComponent,
    MatSlideToggleModule,
    GalaxyTooltipModule,
    GalaxyPipesModule,
    TaskErrorDisclaimerComponent,
    AutomationEditorComponent,
    MatSidenavModule,
    MatMenuModule,
  ],
  templateUrl: './simplified-automation-editor.component.html',
  styleUrls: ['./simplified-automation-editor.component.scss'],
})
export class SimplifiedAutomationEditorComponent implements AfterViewInit, OnDestroy {
  private router = inject(Router);
  private sanitizer = inject(DomSanitizer);
  private snackbarService = inject(SnackbarService);
  private errorHandler = inject(AutomationErrorHandlerService);
  private displayService = inject(DisplayService);
  private translateService = inject(TranslateService);
  private sideMenuService = inject(AutomationsSideDrawerService);

  workflowData = input.required<SimplifiedWorkflowInput>();
  automation = input.required<Automation>();
  disableTriggerEdit = input<boolean>(false);
  startInAdvancedMode = input<boolean>(false);
  dataChange = output<SimplifiedWorkflowInput>();
  automationRunStateChange = output<SimplifiedAutomationRunStateChange>();
  isAdvancedModeChange = output<boolean>();
  editAutomationDetails = output<SimplifiedAutomationEditorEvent>();

  @ViewChildren('dynamicContainer', { read: ViewContainerRef })
  dynamicContainers!: QueryList<ViewContainerRef>;

  taskDefinitionGroups = computed<TaskDefinitionGroup[]>(() => {
    if (!this.workflowData()?.stepData) {
      return [];
    }

    const taskGroups = new Map<string, TaskDefinitionGroup>();

    this.workflowData().stepData.forEach((step) => {
      const taskDefinitionId = step.id?.taskDefinitionId;
      if (!taskDefinitionId) {
        return;
      }
      const componentDef = WORKFLOW_COMPONENTS[taskDefinitionId];

      if (!taskGroups.has(taskDefinitionId)) {
        const isTrigger = step.id?.stepId === 'trigger';
        const componentType = isTrigger ? 'trigger' : 'action';
        const displayName = componentDef.name || taskDefinitionId;
        const taskGroup: TaskDefinitionGroup = {
          taskDefinitionId,
          icon: componentDef.icon,
          iconColor: componentDef.iconColor,
          displayName,
          stepData: [step],
          componentType,
          workflowStep: null,
        };
        taskGroup.workflowStep = this.findWorkflowStep(taskGroup);
        taskGroups.set(taskDefinitionId, taskGroup);
      } else {
        taskGroups.get(taskDefinitionId)?.stepData.push(step);
      }
    });
    return Array.from(taskGroups.values());
  });
  taskDefinitionGroups$ = toObservable(this.taskDefinitionGroups);
  stepNameMap$: Observable<Map<string, string>> = this.taskDefinitionGroups$.pipe(
    tap(() => {
      this.errorHandler.clearValidationErrorMap();
    }),
    switchMap((taskDefinitionGroups) => {
      return combineLatest(
        taskDefinitionGroups.map((group) => {
          if (!group.workflowStep) {
            return of('');
          }
          return this.displayService.getActionTitle(this.automation().namespace, group.workflowStep);
        }),
      );
    }),
    map((titles) => {
      const map = new Map<string, string>();
      titles.forEach((title, index) => {
        map.set(this.taskDefinitionGroups()[index].workflowStep?.id ?? '', title);
      });
      return map;
    }),
  );
  stepNameMap = toSignal(this.stepNameMap$);
  displayNameMap$: Observable<Map<string, string>> = this.taskDefinitionGroups$.pipe(
    tap(() => {
      this.errorHandler.clearValidationErrorMap();
    }),
    switchMap((taskDefinitionGroups) => {
      return combineLatest(
        taskDefinitionGroups.map((group) => {
          if (!group.workflowStep) {
            return of('');
          }
          return this.displayService.getActionSubtitle(
            this.automation().namespace,
            group.taskDefinitionId,
            group.workflowStep,
            this.automation(),
          );
        }),
      );
    }),
    map((titles) => {
      const map = new Map<string, string>();
      titles.forEach((title, index) => {
        map.set(this.taskDefinitionGroups()[index].workflowStep?.id ?? '', title);
      });
      return map;
    }),
  );
  displayNameMap = toSignal(this.displayNameMap$);
  errors$ = this.taskDefinitionGroups$.pipe(
    switchMap((taskDefinitionGroups) => {
      return combineLatest(
        taskDefinitionGroups.map((group) => {
          return this.errorHandler.hasError(group?.workflowStep?.id ?? '');
        }),
      );
    }),
    withLatestFrom(this.taskDefinitionGroups$),
    map(([errors, taskDefinitionGroups]) => {
      const map = new Map<string, boolean>();
      errors.forEach((error, index) => {
        map.set(taskDefinitionGroups[index].workflowStep?.id || '', error);
      });
      return map;
    }),
  );
  errors = toSignal(this.errors$);
  hasErrors = computed<boolean>(() => {
    return Array.from(this.errors()?.entries() ?? []).some(([_, error]) => !!error);
  });
  errorIconColor = ERROR_ICON_COLOR;
  startExpanded = signal(new Map<string, boolean>());

  automationName = computed<string>(() => {
    return this.automation()?.name || '';
  });
  automationDescription = computed<string>(() => {
    return this.automation()?.description || '';
  });
  isAutomationRunning = computed<boolean>(() => {
    return this.automation().runningState === RunningState.RUNNING_STATE_RUNNING;
  });
  isAutomationDraining = computed<boolean>(() => {
    return this.automation().runningState === RunningState.RUNNING_STATE_DRAINING;
  });
  private viewInitialized = signal(false);

  automationRunStateControl = new FormControl<boolean>(false);

  // Advanced mode state management - signal for tracking state
  isAdvancedMode = signal<boolean>(false);
  getWorkflowUrl = computed(() => {
    const automationId = this.automation()?.id;
    if (!automationId) {
      return this.sanitizer.bypassSecurityTrustResourceUrl('');
    }
    // Return the sanitized URL to the automation workflow page
    const url = `/automations/automation/${automationId}/workflow`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  });

  componentRefs: ComponentRef<any>[] = [];

  constructor() {
    // initial expansion is delayed on purpose to avoid flickering
    effect(() => {
      setTimeout(() => {
        const map = new Map<string, boolean>();
        let hasErrors = false;
        for (const group of this.taskDefinitionGroups()) {
          if (this.errors()?.get(group.workflowStep?.id ?? '')) {
            map.set(group.workflowStep?.id ?? '', true);
            hasErrors = true;
          }
        }
        if (!hasErrors) {
          if (this.disableTriggerEdit() && this.taskDefinitionGroups().length > 1) {
            map.set(this.taskDefinitionGroups()[1].workflowStep?.id ?? '', true);
          } else if (!this.disableTriggerEdit() && this.taskDefinitionGroups().length > 0) {
            map.set(this.taskDefinitionGroups()[0].workflowStep?.id ?? '', true);
          } else if (this.taskDefinitionGroups().length > 1) {
            map.set(this.taskDefinitionGroups()[1].workflowStep?.id ?? '', true);
          }
        }
        this.startExpanded.set(map);
      }, 500);
    });
    effect(() => {
      if (!this.isAdvancedMode() && this.viewInitialized() && this.taskDefinitionGroups()) {
        this.sideMenuService.close();
        // renderPanelComponents should run after the view is initialized again
        setTimeout(() => {
          this.renderPanelComponents();
        }, 100);
      }
    });
    effect(() => {
      if (this.startInAdvancedMode()) {
        this.isAdvancedMode.set(this.startInAdvancedMode());
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.workflowData().stepData?.length === 1) {
      this.isAdvancedMode.set(true);
    }
    this.viewInitialized.set(true);
    this.automationRunStateControl.setValue(this.isAutomationRunning());
  }

  ngOnDestroy(): void {
    this.componentRefs.forEach((ref) => ref.destroy());
  }

  private renderPanelComponents(): void {
    this.componentRefs = [];
    const containers = this.dynamicContainers.toArray();

    for (const [i, group] of this.taskDefinitionGroups().entries()) {
      const container = containers[i];

      if (container) {
        container.clear();
        const componentRef = this.createPanelComponent(group, container);
        if (componentRef) {
          this.componentRefs.push(componentRef);
        }
      }
    }
  }

  private createPanelComponent(group: TaskDefinitionGroup, container: ViewContainerRef): ComponentRef<any> | null {
    try {
      if (!group.workflowStep) {
        console.warn(`No workflow step found for group ${group.taskDefinitionId}, skipping component creation`);
        return null;
      }

      let componentRef: ComponentRef<any>;
      if (group.componentType === 'trigger') {
        const componentDef = WORKFLOW_COMPONENTS[group.taskDefinitionId];
        componentRef = container.createComponent(componentDef.panel as any);

        // Set trigger-specific properties
        const instance = componentRef.instance as CommonTriggerPanelComponent;
        instance.taskDefinitionId = group.taskDefinitionId;
        instance.automation = this.automation();
        instance.triggerChoices = this.automation().triggerStep.choices;
        instance.step = group.workflowStep;
        instance.isWaitingForEvent = false;
        instance.waitForEventData = '';
      } else {
        const componentDef = WORKFLOW_COMPONENTS[group.taskDefinitionId];
        componentRef = container.createComponent(componentDef.panel as any);

        // Set action-specific properties
        const instance = componentRef.instance as CommonActionPanelComponent;
        instance.step = group.workflowStep;
        instance.title = group.displayName;
        instance.automation = this.automation();
        instance.precedingSteps = [];
        instance.branch = 'NONE';
        instance.taskDefinitionId = group.taskDefinitionId;
        instance.isGoal = false;
      }

      // Trigger change detection
      componentRef.changeDetectorRef.detectChanges();

      return componentRef;
    } catch (error) {
      console.error(`Failed to create component for ${group.taskDefinitionId}:`, error);
      return null;
    }
  }

  private findWorkflowStep(group: TaskDefinitionGroup): WorkflowStepInterface | null {
    // Find the existing workflow step based on step ID
    const firstStep = group.stepData[0];
    const stepId = firstStep.id?.stepId;

    if (!stepId) {
      return null;
    }

    const automation = this.automation();

    // Look in workflow steps
    if (automation.workflow) {
      const foundStep = automation.workflow.find((step) => step.id === stepId);
      if (foundStep) {
        return foundStep;
      }
    }

    // Look in trigger step
    if (automation.triggerStep?.id === stepId) {
      return automation.triggerStep;
    }

    // Look in trigger filters
    if (automation.triggerFilters) {
      const foundTriggerFilter = automation.triggerFilters.find((step) => step.id === stepId);
      if (foundTriggerFilter) {
        return foundTriggerFilter;
      }
    }

    // Return null if step not found
    return null;
  }

  private buildStepDataJson(stepDataItems: StepDataInterface[]): string {
    const dataObject: any = {};

    stepDataItems.forEach((item) => {
      if (item.data && item.id?.key) {
        const { key } = item.id;
        if (item.data.stringValue !== undefined) {
          dataObject[key] = item.data.stringValue;
        } else if (item.data.numberValue !== undefined) {
          dataObject[key] = item.data.numberValue;
        } else if (item.data.booleanValue !== undefined) {
          dataObject[key] = item.data.booleanValue;
        }
      }
    });

    return JSON.stringify(dataObject);
  }

  collectDataAndEmit(): void {
    const updatedStepData: StepDataInterface[] = [];
    try {
      this.componentRefs.forEach((componentRef, index) => {
        const group = this.taskDefinitionGroups()[index];
        const instance = componentRef.instance;

        const panelData = instance.getData();
        if (this.isAutomationRunning() || this.isAutomationDraining()) {
          throw new Error(
            this.translateService.instant('AUTOMATIONS.EDITOR.SIMPLIFIED_EDITOR.STOP_AUTOMATION_BEFORE_EDITING'),
          );
        }
        if (!instance.isFormValid()) {
          throw new Error(this.translateService.instant('AUTOMATIONS.EDITOR.SIMPLIFIED_EDITOR.FILL_REQUIRED_FIELDS'));
        }

        group.stepData.forEach((originalStep) => {
          const key = originalStep.id?.key;
          if (!key) return;

          const value = panelData[key];

          const updatedStep: StepDataInterface = {
            id: originalStep.id,
          };

          if (value !== undefined && value !== null) {
            if (typeof value === 'string') {
              updatedStep.data = { stringValue: value };
            } else if (typeof value === 'number') {
              updatedStep.data = { numberValue: value };
            } else if (typeof value === 'boolean') {
              updatedStep.data = { booleanValue: value };
            }
          }

          updatedStepData.push(updatedStep);
        });
      });
      this.dataChange.emit({ stepData: updatedStepData });
    } catch (error) {
      this.snackbarService.openErrorSnack(
        error instanceof Error
          ? error.message
          : this.translateService.instant('AUTOMATIONS.EDITOR.SIMPLIFIED_EDITOR.UNKNOWN_ERROR'),
      );
    }
  }

  onToggleChange(event: MatSlideToggleChange): void {
    this.automationRunStateChange.emit({ automation: this.automation(), turnOn: event.checked });
  }

  stopDraining(): void {
    this.automationRunStateChange.emit({ automation: this.automation(), turnOn: false });
  }

  toggleAdvancedMode(): void {
    this.isAdvancedMode.set(!this.isAdvancedMode());
    this.isAdvancedModeChange.emit(this.isAdvancedMode());
  }

  navigateToFullEditor(): void {
    if (!this.automation()?.id) {
      return;
    }
    this.router.navigate([`automations/automation/${this.automation().id}`]);
  }

  emitAutomationEditDetailsEvent(): void {
    this.editAutomationDetails.emit({ automation: this.automation() });
  }
}
